import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';  // ← FIXED: Added AnimatePresence import
import {
  Users,
  MapPin,
  Clock,
  AlertTriangle,
  Heart,
  Target,
  Flag,
  CheckCircle,
  XCircle,
  Navigation,
  Radio,
  Siren,
  Plus,
  Minus
} from 'lucide-react';

interface TrappedCluster {
  id: string;
  zone: string;
  location: string;
  count: number;
  priority: number;
  status: 'critical' | 'high' | 'medium' | 'low';
  coordinates: { x: number; y: number };
  estimatedTime: number;
  assignedTeam?: string;
  conditions: string[];
}

interface RescueTeam {
  id: string;
  name: string;
  status: 'available' | 'responding' | 'on-scene' | 'transporting';
  location: string;
  eta?: number;
  capacity: number;
}

const TrappedHeatmap: React.FC = () => {
  const [selectedCluster, setSelectedCluster] = useState<TrappedCluster | null>(null);
  const [viewMode, setViewMode] = useState<'heatmap' | 'list' | 'priority'>('heatmap');
  const [showAssignment, setShowAssignment] = useState(false);

  const clusters: TrappedCluster[] = [
    {
      id: '1',
      zone: 'Zone A',
      location: 'Building #23, 3rd Floor',
      count: 47,
      priority: 1,
      status: 'critical',
      coordinates: { x: 30, y: 40 },
      estimatedTime: 25,
      conditions: ['Rooftop', 'Flooded', 'Children present']
    },
    {
      id: '2',
      zone: 'Zone A',
      location: 'Community Center, Roof',
      count: 23,
      priority: 2,
      status: 'high',
      coordinates: { x: 45, y: 35 },
      estimatedTime: 35,
      conditions: ['Elderly', 'Medical needs']
    },
    {
      id: '3',
      zone: 'Zone B',
      location: 'Market Complex, Block C',
      count: 12,
      priority: 3,
      status: 'high',
      coordinates: { x: 60, y: 55 },
      estimatedTime: 45,
      conditions: ['Fire nearby', 'Smoke inhalation']
    },
    {
      id: '4',
      zone: 'Zone B',
      location: 'Residential Building #45',
      count: 8,
      priority: 4,
      status: 'medium',
      coordinates: { x: 70, y: 30 },
      estimatedTime: 55,
      conditions: ['Partial collapse', 'Stable']
    },
    {
      id: '5',
      zone: 'Zone C',
      location: 'School Building, 2nd Floor',
      count: 15,
      priority: 5,
      status: 'medium',
      coordinates: { x: 55, y: 70 },
      estimatedTime: 60,
      conditions: ['Children', 'Teachers with them']
    },
    {
      id: '6',
      zone: 'Zone C',
      location: 'Apartment Complex, Basement',
      count: 5,
      priority: 6,
      status: 'low',
      coordinates: { x: 40, y: 50 },
      estimatedTime: 75,
      conditions: ['Stable', 'Accessible']
    }
  ];

  const teams: RescueTeam[] = [
    { id: 't1', name: 'Alpha Team', status: 'responding', location: 'Zone A', eta: 5, capacity: 8 },
    { id: 't2', name: 'Bravo Team', status: 'available', location: 'Zone B', capacity: 6 },
    { id: 't3', name: 'Charlie Team', status: 'on-scene', location: 'Zone C', capacity: 8 },
    { id: 't4', name: 'Delta Team', status: 'transporting', location: 'Zone B', eta: 15, capacity: 6 }
  ];

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const getTeamStatusColor = (status: string) => {
    switch(status) {
      case 'available': return 'text-green-500 border-green-500 bg-green-500/10';
      case 'responding': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10 animate-pulse';
      case 'on-scene': return 'text-blue-500 border-blue-500 bg-blue-500/10';
      case 'transporting': return 'text-purple-500 border-purple-500 bg-purple-500/10';
      default: return 'text-gray-500';
    }
  };

  const getIntensityColor = (count: number) => {
    if (count > 30) return 'bg-red-500';
    if (count > 20) return 'bg-orange-500';
    if (count > 10) return 'bg-yellow-500';
    if (count > 5) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const totalTrapped = clusters.reduce((sum, c) => sum + c.count, 0);
  const criticalCount = clusters.filter(c => c.status === 'critical').reduce((sum, c) => sum + c.count, 0);
  const assignedCount = clusters.filter(c => c.assignedTeam).length;

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Heart className="text-red-500 animate-pulse" />
            TRAPPED PERSON HEATMAP
          </h2>
          <p className="text-xs text-gray-400 mt-1">Real-time victim cluster tracking</p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewMode === 'heatmap'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            Heatmap
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewMode === 'list'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            List
          </button>
          <button
            onClick={() => setViewMode('priority')}
            className={`px-3 py-1 rounded-lg text-sm ${
              viewMode === 'priority'
                ? 'bg-cyan-600 text-white'
                : 'bg-gray-800 text-gray-400'
            }`}
          >
            Priority
          </button>
        </div>
      </div>

      {/* Heatmap Visualization */}
      {viewMode === 'heatmap' && (
        <div className="relative bg-black/50 rounded-lg p-4 mb-6 border border-cyan-500/30 h-64">
          {/* Grid Background */}
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(0,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} />

          {/* Trapped Clusters */}
          {clusters.map((cluster) => (
            <motion.div
              key={cluster.id}
              className="absolute cursor-pointer group"
              style={{ left: `${cluster.coordinates.x}%`, top: `${cluster.coordinates.y}%` }}
              initial={{ scale: 0 }}
              animate={{ scale: Math.min(cluster.count / 10, 3) }}
              whileHover={{ scale: Math.min(cluster.count / 10, 3) * 1.2 }}
              onClick={() => setSelectedCluster(cluster)}
            >
              <div className={`w-12 h-12 rounded-full ${getIntensityColor(cluster.count)} opacity-60 group-hover:opacity-80 transition-all flex items-center justify-center`}
                   style={{ transform: 'translate(-50%, -50%)' }}>
                <span className="text-white text-xs font-bold">{cluster.count}</span>
              </div>
              
              {/* Tooltip */}
              <div className="absolute top-0 left-0 transform -translate-x-1/2 -translate-y-8 bg-black/90 border border-cyan-500 rounded px-2 py-1 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                {cluster.zone}: {cluster.count} trapped • Priority {cluster.priority}
              </div>
            </motion.div>
          ))}

          {/* Zone Labels */}
          {['Zone A', 'Zone B', 'Zone C'].map((zone, i) => (
            <div
              key={zone}
              className="absolute text-xs font-bold text-gray-400"
              style={{ left: `${20 + i * 25}%`, top: '80%' }}
            >
              {zone}
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll mb-6">
          {clusters.map((cluster) => (
            <motion.div
              key={cluster.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 cursor-pointer"
              onClick={() => setSelectedCluster(cluster)}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold">{cluster.zone}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(cluster.status)}`}>
                      {cluster.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{cluster.location}</p>
                </div>
                <div className="text-2xl font-bold text-red-500">{cluster.count}</div>
              </div>

              <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                <Clock size={12} />
                ETA: {cluster.estimatedTime} min
                {cluster.assignedTeam && (
                  <>
                    <span>•</span>
                    <CheckCircle size={12} className="text-green-500" />
                    Assigned to {cluster.assignedTeam}
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-1">
                {cluster.conditions.map((condition, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 bg-gray-800 rounded">
                    {condition}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Priority View */}
      {viewMode === 'priority' && (
        <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scroll mb-6">
          {[...clusters].sort((a, b) => a.priority - b.priority).map((cluster) => (
            <motion.div
              key={cluster.id}
              className="flex items-center gap-3 p-3 bg-black/30 rounded-lg border border-gray-700"
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${getIntensityColor(cluster.count)}`}>
                {cluster.priority}
              </div>
              <div className="flex-1">
                <div className="flex justify-between">
                  <span className="font-bold text-sm">{cluster.zone}</span>
                  <span className="text-red-500 font-bold">{cluster.count}</span>
                </div>
                <p className="text-xs text-gray-400">{cluster.location}</p>
              </div>
              <button className="px-2 py-1 bg-cyan-600/20 text-cyan-500 rounded text-xs">
                Assign
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {/* Available Teams */}
      <div className="mb-4">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Users size={14} className="text-cyan-500" />
          Available Rescue Teams
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {teams.map((team) => (
            <div key={team.id} className="p-2 bg-black/30 rounded-lg border border-gray-700">
              <div className="flex justify-between items-start mb-1">
                <span className="text-xs font-bold">{team.name}</span>
                <span className={`text-[10px] px-1 py-0.5 rounded ${getTeamStatusColor(team.status)}`}>
                  {team.status}
                </span>
              </div>
              <div className="flex justify-between text-[10px] text-gray-400">
                <span>{team.location}</span>
                <span>Cap: {team.capacity}</span>
              </div>
              {team.eta && (
                <div className="text-[10px] text-yellow-500 mt-1">ETA: {team.eta} min</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Cluster Detail Modal */}
      <AnimatePresence>
        {selectedCluster && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setSelectedCluster(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-red-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-red-500 mb-4">Trapped Cluster Details</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-400">Location</p>
                    <p className="font-bold">{selectedCluster.zone} - {selectedCluster.location}</p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${getStatusColor(selectedCluster.status)}`}>
                    {selectedCluster.count} People
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-black/30 rounded-lg">
                    <p className="text-xs text-gray-400">Priority</p>
                    <p className="text-2xl font-bold text-red-500">{selectedCluster.priority}</p>
                  </div>
                  <div className="p-3 bg-black/30 rounded-lg">
                    <p className="text-xs text-gray-400">Est. Time</p>
                    <p className="text-2xl font-bold text-yellow-500">{selectedCluster.estimatedTime}min</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-2">Conditions</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedCluster.conditions.map((cond, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-cyan-600/20 rounded">
                        {cond}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-400 mb-2">Assign Rescue Team</p>
                  <select className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm mb-3">
                    <option>Select a team...</option>
                    {teams.filter(t => t.status === 'available').map(team => (
                      <option key={team.id}>{team.name} (Capacity: {team.capacity})</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button className="flex-1 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-3 rounded-lg">
                    DEPLOY TEAM
                  </button>
                  <button
                    onClick={() => setSelectedCluster(null)}
                    className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg"
                  >
                    CLOSE
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Footer */}
      <div className="grid grid-cols-4 gap-2 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-red-500">{totalTrapped}</div>
          <div className="text-[10px] text-gray-400">Total Trapped</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-500">{criticalCount}</div>
          <div className="text-[10px] text-gray-400">Critical</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">{assignedCount}</div>
          <div className="text-[10px] text-gray-400">Assigned</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-cyan-500">{clusters.length}</div>
          <div className="text-[10px] text-gray-400">Clusters</div>
        </div>
      </div>
    </div>
  );
};

export default TrappedHeatmap;